/**
 * The shop's colour palette, as the dashboard sees it.
 *
 * The palette lives in the backend (`GET /v1/admin/colors`), not here. It is
 * shop data: a merchant who stocks a frame in a colour nobody anticipated adds
 * it once and it is there in every later Add and Edit workflow, after a logout,
 * a browser restart and a service restart. A constant in this bundle could do
 * none of that, so this file holds only the matching rules.
 *
 * What this file will not do
 * --------------------------
 * It will not guess. There is no nearest-hex matching and nothing looks at a
 * photograph: those would put a colour on a real storefront that nobody chose.
 * A name the palette does not hold is reported as unmatched so the
 * administrator can add it deliberately through Add New Color.
 *
 * The same name may be several colours
 * ------------------------------------
 * A shop can sell two visibly different blacks. Colours are therefore
 * identified by name *and* swatch, and a folder named "black" that lands on
 * more than one of them is answered by one explicit click rather than by taking
 * the first row.
 */

/** One colour the shop can put on a product. No database id is exposed. */
export interface PaletteColor {
  name: string;
  /** The normalized_color_key the product contract stores on a variant. */
  color_key: string;
  swatch_hex: string;
  /** False for colours an administrator added. */
  is_builtin: boolean;
}

/**
 * Fold a folder name for comparison.
 *
 * Absorbs only presentation differences: surrounding whitespace, inner runs of
 * spaces, underscores, hyphens and capitalisation. The real photography library
 * contains "black ", "Tortoiseshell" and "dark gray" as folder names, and all
 * three name colours the palette holds.
 *
 * Nothing here reinterprets a name — "blue cheetah" folds to "blue-cheetah",
 * which is simply a colour the palette may or may not have.
 */
export function normalizeColorFolderName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export type ColorMatch =
  | { kind: "matched"; color: PaletteColor; detectedName: string }
  | {
      kind: "ambiguous";
      candidates: PaletteColor[];
      detectedName: string;
      normalized: string;
    }
  | { kind: "unknown"; detectedName: string; normalized: string };

/**
 * Map one colour folder name onto the palette.
 *
 * Exactly one candidate is selected automatically — that is unambiguous and
 * making the operator confirm it would be noise. Several candidates means the
 * shop has that name in more than one shade, and none means the palette has no
 * such colour yet; both are answered by the administrator.
 */
export function matchColorFolderName(
  name: string,
  palette: readonly PaletteColor[],
): ColorMatch {
  const detectedName = name.trim();
  const normalized = normalizeColorFolderName(name);

  const candidates = palette.filter(
    (color) =>
      normalizeColorFolderName(color.color_key) === normalized ||
      normalizeColorFolderName(color.name) === normalized,
  );

  if (candidates.length === 1) {
    return { kind: "matched", color: candidates[0]!, detectedName };
  }
  if (candidates.length > 1) {
    return { kind: "ambiguous", candidates, detectedName, normalized };
  }
  return { kind: "unknown", detectedName, normalized };
}

/** Six hex digits, as the backend requires. Used to keep the form honest before
 *  it submits; the backend validates again and is the authority. */
export const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export function isValidHex(value: string): boolean {
  return HEX_PATTERN.test(value.trim());
}

/** A colour's identity for comparison and React keys: name and swatch together,
 *  because either alone can repeat across the palette. */
export function colorIdentity(color: PaletteColor): string {
  return `${color.color_key}|${color.swatch_hex.toUpperCase()}`;
}
